import { BigNumber } from "ethers";
import { defaultAbiCoder } from "ethers/lib/utils";
import { useMerkleProof } from "modules/bridge/api/useMerkleProof";
import { Erc20__factory } from "modules/bridge/types/contracts/erc-20";
import { Bridge__factory } from "../types/contracts/bridge";
import { Address } from "viem";
import {
  useReadContracts,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi";

export type ClaimArgs = {
  bridge: {
    amount: BigNumber;
    depositCount: number;
    depositTxHash: string;
    destinationAddress: Address;
    from: { networkId: number; bridgeContractAddress: Address };
    globalIndex: number;
    to: { key: string; networkId: number; bridgeContractAddress: Address };
    token: { address: Address; chainId: number };
    tokenOriginNetwork: number;
  };
};

export const useClaim = ({ bridge }: ClaimArgs) => {
  const wagmiMetadataContract = {
    address: bridge.token.address,
    abi: Erc20__factory.abi,
  } as const;

  const { data: tokenMetadata } = useReadContracts({
    contracts: [
      {
        ...wagmiMetadataContract,
        functionName: "name",
      },
      {
        ...wagmiMetadataContract,
        functionName: "symbol",
      },
      {
        ...wagmiMetadataContract,
        functionName: "decimals",
      },
    ],
  });

  const { writeContract, data: hash, ...rest } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed } =
    useWaitForTransactionReceipt({
      hash,
      query: { enabled: Boolean(hash), retry: true },
    });

  const { data: merkleProofData } = useMerkleProof({
    depositCount: bridge.depositCount,
    networkId: 1,
  });
  // const isL2Claim = bridge.to.key === "polygon-zkevm";

  const claim = async () => {
    return writeContract({
      address: bridge.to.bridgeContractAddress,
      abi: Bridge__factory.abi,
      functionName: "claimAsset",
      args: [
        merkleProofData?.merkleProof,
        merkleProofData?.rollupMerkleProof,
        bridge.globalIndex,
        merkleProofData?.mainExitRoot,
        merkleProofData?.rollupExitRoot,
        bridge.tokenOriginNetwork,
        bridge.token.address,
        bridge.to.networkId,
        bridge.destinationAddress,
        bridge.amount,
        defaultAbiCoder.encode(
          ["string", "string", "uint8"],
          [
            tokenMetadata?.[0]?.result,
            tokenMetadata?.[1]?.result,
            tokenMetadata?.[2]?.result,
          ]
        ),
      ],
    });
  };

  return {
    claim,
    ...rest,
    isConfirming,
    isConfirmed,
  };
};
